import { axiosApiInstance } from "../axios";

const DEFAULT_ERROR_MESSAGE = "Could not generate the invoice PDF.";

export class OrderInvoiceApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = "OrderInvoiceApiError";
  }
}

function fallbackFilename(orderId: string, orderNumber?: string | null): string {
  const identifier = orderNumber?.trim() || `order-${orderId}`;
  const safeIdentifier = identifier.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-|-$/g, "");

  return `invoice-${safeIdentifier}.pdf`;
}

function filenameFromDisposition(
  disposition: unknown,
  orderId: string,
  orderNumber?: string | null
): string {
  const fallback = fallbackFilename(orderId, orderNumber);
  if (typeof disposition !== "string") return fallback;

  const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encodedFilename) {
    try {
      return decodeURIComponent(encodedFilename.trim());
    } catch {
      // Fall through to the regular filename or safe default.
    }
  }

  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]?.trim();
  return filename || fallback;
}

async function errorMessageFromResponse(data: unknown): Promise<string> {
  let payload = data;

  if (payload instanceof Blob) {
    try {
      payload = JSON.parse(await payload.text());
    } catch {
      return DEFAULT_ERROR_MESSAGE;
    }
  } else if (typeof payload === "string") {
    const stringPayload = payload;
    try {
      payload = JSON.parse(stringPayload);
    } catch {
      return stringPayload || DEFAULT_ERROR_MESSAGE;
    }
  }

  if (payload && typeof payload === "object" && "message" in payload) {
    const message = (payload as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }

  return DEFAULT_ERROR_MESSAGE;
}

function isApiError(error: unknown): error is {
  isAxiosError: true;
  response?: { data?: unknown; status?: number };
} {
  return Boolean(
    error &&
    typeof error === "object" &&
    "isAxiosError" in error &&
    (error as { isAxiosError?: unknown }).isAxiosError === true
  );
}

export async function getOrderInvoicePdf(
  orderId: string,
  orderNumber?: string | null
): Promise<{
  blob: Blob;
  filename: string;
}> {
  try {
    const response = await axiosApiInstance.post<Blob>(
      `printOrder/${orderId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken") ?? ""}`,
        },
        responseType: "blob",
      }
    );

    return {
      blob: response.data,
      filename: filenameFromDisposition(
        response.headers["content-disposition"],
        orderId,
        orderNumber
      ),
    };
  } catch (error: unknown) {
    if (isApiError(error)) {
      throw new OrderInvoiceApiError(
        await errorMessageFromResponse(error.response?.data),
        error.response?.status
      );
    }

    throw new OrderInvoiceApiError(DEFAULT_ERROR_MESSAGE);
  }
}

export function downloadOrderInvoicePdf(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const downloadLink = document.createElement("a");
  downloadLink.href = objectUrl;
  downloadLink.download = filename;
  downloadLink.style.display = "none";
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
